import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SoulfileApiErrorTypes, SoulfileApiError } from './soulfile.errors';
import { SoulfileProject } from 'src/app/admin/models/soulfile-project.model';
import { Soulfile } from 'src/app/admin/models/soulfile.model';
import { toHttpParams } from '../project/helpers/to-http-params';

const PROJECTS_BASE_URL = '/admin/soulfile-projects';
const PROJECT_SUMMARIES_URL = '/admin/soulfile-project-summaries';
const SOULFILES_BASE_URL = '/admin/soulfiles';
const MAX_SOULFILES_PER_PAGE = 50;

@Injectable()
export class SoulfileService {
  constructor(private http: HttpClient) {}

  loadProjects(): Observable<SoulfileProject[]> {
    const url = `${environment.apiConfig.url}${PROJECT_SUMMARIES_URL}`;

    return this.http
      .get<SoulfileProject[]>(url)
      .pipe(
        catchError((err) =>
          throwError(new SoulfileApiError(SoulfileApiErrorTypes.LoadSoulfileProjectsError, err)),
        ),
      );
  }

  loadProjectsCsv(): Observable<Blob> {
    const url = `${environment.apiConfig.url}${PROJECT_SUMMARIES_URL}`;
    const params = toHttpParams({ dataFormat: 'csv' });

    return this.http
      .get(url, {
        params,
        headers: { Accept: 'application/octet-stream' },
        responseType: 'blob',
      })
      .pipe(
        catchError((err) =>
          throwError(new SoulfileApiError(SoulfileApiErrorTypes.LoadSoulfileProjectsCsvError, err)),
        ),
      );
  }

  createProject(avatarName: string): Observable<SoulfileProject> {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}`;
    const newProject = { avatarName };

    return this.http.post<SoulfileProject>(url, newProject).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 409) {
          return throwError(
            new SoulfileApiError(SoulfileApiErrorTypes.CreateSoulfileProjectConflictError, err),
          );
        }
        return throwError(
          new SoulfileApiError(SoulfileApiErrorTypes.CreateSoulfileProjectError, err),
        );
      }),
    );
  }

  createProjectWithOverride(
    avatarName: string,
    attributes: { [key: string]: string }[],
  ): Observable<SoulfileProject> {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}`;
    const newProject = {
      avatarName,
      presoulOverride: {
        attributes,
        version: 'v1',
      },
    };

    return this.http.post<SoulfileProject>(url, newProject).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 409) {
          return throwError(
            new SoulfileApiError(SoulfileApiErrorTypes.CreateSoulfileProjectConflictError, err),
          );
        }
        return throwError(
          new SoulfileApiError(SoulfileApiErrorTypes.CreateSoulfileProjectError, err),
        );
      }),
    );
  }

  updateProject(id: number, project: Partial<SoulfileProject>): Observable<void> {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}/${id}`;
    const newProject = { ...project };

    return this.http.patch<void>(url, newProject).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 409) {
          return throwError(
            new SoulfileApiError(SoulfileApiErrorTypes.UpdateSoulfileProjectConflictError, err),
          );
        }
        return throwError(
          new SoulfileApiError(SoulfileApiErrorTypes.UpdateSoulfileProjectError, err),
        );
      }),
    );
  }

  deleteProject(projectId: number): Observable<any> {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}/${projectId}`;

    return this.http
      .delete(url)
      .pipe(
        catchError((err) =>
          throwError(new SoulfileApiError(SoulfileApiErrorTypes.DeleteSoulfileProjectError, err)),
        ),
      );
  }

  loadSoulfiles(projectId: number) {
    const url = `${environment.apiConfig.url}${SOULFILES_BASE_URL}`;
    const params = new HttpParams({
      fromObject: {
        'filter[limit]': String(MAX_SOULFILES_PER_PAGE),
        'filter[order]': 'datetimeAdded DESC',
        'filter[where][soulfileProjectId]': String(projectId),
      },
    });

    return this.http
      .get<Soulfile[]>(url, { params })
      .pipe(
        catchError((err) =>
          throwError(new SoulfileApiError(SoulfileApiErrorTypes.LoadSoulfilesError, err)),
        ),
      );
  }

  loadSoulfile(soulfileId: number) {
    const url = `${environment.apiConfig.url}${SOULFILES_BASE_URL}/${soulfileId}`;

    return this.http
      .get<Soulfile>(url)
      .pipe(
        catchError((err) =>
          throwError(new SoulfileApiError(SoulfileApiErrorTypes.SoulfileNotFoundError, err)),
        ),
      );
  }

  getSoulfileProject(projectId: number) {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}/${projectId}`;

    return this.http
      .get<SoulfileProject>(url)
      .pipe(
        catchError((err) =>
          throwError(new SoulfileApiError(SoulfileApiErrorTypes.SoulfileProjectNotFoundError, err)),
        ),
      );
  }

  createSoulfile(
    soulfileProjectId: number,
    description: string,
    values: Record<string, unknown>,
  ): Observable<Soulfile> {
    const url = `${environment.apiConfig.url}${SOULFILES_BASE_URL}`;
    const newSoulfile = {
      soulfileProjectId,
      description,
      values,
    };

    return this.http
      .post<Soulfile>(url, newSoulfile)
      .pipe(
        catchError((err) =>
          throwError(new SoulfileApiError(SoulfileApiErrorTypes.CreateSoulfileError, err)),
        ),
      );
  }
}
